// Memoization cache for factorial calculations
const factorialCache = new Map<number, number>();

function logFactorial(n: number): number {
  // Calculates ln(n!)
  if (n <= 1) return 0;

  // Check cache first
  if (factorialCache.has(n)) {
    return factorialCache.get(n)!;
  }

  // Use more efficient calculation
  let result = 0;
  for (let i = 2; i <= n; i++) {
    result += Math.log(i);
  }

  // Cache the result
  factorialCache.set(n, result);
  return result;
}

function logBinomialCoeff(n: number, successes: number): number {
  // We use logs here temporarily to avoid overflow, since n! is jumbo big
  // Gets binomial coefficient (n choose k)
  return logFactorial(n) - logFactorial(successes) - logFactorial(n - successes);
}

function binomialProb(n: number, successes: number, probability: number): number {
  // Get discrete probability of successes in n trials with success probability p
  // Exponentiate after calculations to cancel out the logs
  return Math.exp(
    logBinomialCoeff(n, successes) +
      successes * Math.log(probability) +
      (n - successes) * Math.log(1 - probability),
  );
}

function getBinomialPValue(successes: number, n: number, probability: number): number {
  // Get probability of getting as many or more successes than observed (or inverse)
  const expectedSuccessesByBenchmark = n * probability;

  if (successes === expectedSuccessesByBenchmark) return 1;

  let pValue = 0;
  const isLeftTail = successes < expectedSuccessesByBenchmark;
  const start = isLeftTail ? 0 : successes;
  const end = isLeftTail ? successes : n;

  // Pre-calculate common values to avoid repeated computation
  // Note: These could be used for further optimization if needed

  for (let dx = start; dx <= end; ++dx) {
    const prob = binomialProb(n, dx, probability);
    pValue += prob;

    // Early termination: if probability becomes very small, stop
    if (prob < 1e-10 && pValue > 0.001) {
      break;
    }
  }

  return Math.min(pValue, 1);
}

export function isStatisticallySignificantBinomialTest({
  conceptPercentage,
  conceptSampleSize,
  benchmarkPercentage,
}: {
  conceptPercentage: number;
  conceptSampleSize: number;
  benchmarkPercentage: number;
}): boolean {
  const successes = Math.round((conceptPercentage * conceptSampleSize) / 100);
  const benchmarkProbability = benchmarkPercentage / 100;
  const n = conceptSampleSize;

  // Check minimum sample size
  if (n < 30) return false;

  // Check significance via binomial test
  const pValue = getBinomialPValue(successes, n, benchmarkProbability);

  return pValue < 0.05;
}
